import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { calculateAge } from '../components/PatientCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ThresholdsTab from '../components/ThresholdsTab';
import ClinicalFindingsSection from '../components/ClinicalFindings_Section';
import MedicalHistorySection from '../components/MedicalHistory_Section';
import ContactsSection from '../components/ContactsSection';
import MedicationManager from '../components/MedicationManager';
import MedicationTrackerChart from '../components/MedicationTrackerChart';
import AppointmentsTab from '../components/AppointmentsTab';
import LabResultsSection from '../components/LabResultsSection';
import NotesSection from '../components/NotesSection';
import VitalsChart from '../components/VitalsChart';
import AlertsTab from '../components/AlertsTab';

import { useAuth } from '../context/AuthContext';

import { Pencil, Trash2, X, UserPlus, Check, AlertTriangle, ChevronLeft, BarChart3, Plus, Smartphone, Phone, Mail, MapPin } from 'lucide-react';
import { getToken } from '../utils/auth';
import { parseBackendDate } from '../utils/datetime';
import Modal, { DialogHeader, DialogFooter, DialogBody } from '../components/ui/Modal';
import UnderlineTabs from '../components/ui/UnderlineTabs';
import { KeepAlivePanel, useVisitedTabs } from '../motion';
import { VITAL_CONFIG, VitalIcon } from '../components/vitalsConfig';

const PatientDetailPage = () => {
    const API_BASE_URL = process.env.REACT_APP_API_URL;

    const location = useLocation();

    const { user } = useAuth();
    const loggedInUserId = user?.user_id ?? null;
    const [clinicians, setClinicians] = useState(null);
    const [clinicianId, setClinicianId] = useState(null);
    const [currentClinician, setCurrentClinician] = useState(null);
    const [showNurseAssignModal, setShowNurseAssignModal] = useState(false);
    const [selectedNursesToAssign, setSelectedNursesToAssign] = useState([]);
    const [availableNurses, setAvailableNurses] = useState([]);
    const [showDoctorAssignModal, setShowDoctorAssignModal] = useState(false);
    const [selectedDoctorsToAssign, setSelectedDoctorsToAssign] = useState([]);
    const [availableDoctors, setAvailableDoctors] = useState([]);

    const { patientId } = useParams();
    const navigate = useNavigate();

    const [patient, setPatient] = useState(null);
    const [pmrn, setPmrn] = useState(null);

    const [loading, setLoading] = useState(true);

    const defaultTab = location.state?.activeTab || "overview";
    const [activeTab, setActiveTab] = useState(defaultTab);
    const visitedTabs = useVisitedTabs(activeTab);

    const [allMedicalHistory, setAllMedicalHistory] = useState([]);
    const [allclinical_findings, setAllClinical_findings] = useState([]);

    const [allMedications, setAllMedications] = useState({ current: [], history: [] });
    const [allAppointments, setAllAppointments] = useState([]);
    const [allReadings, setAllReadings] = useState([]);
    
    const [latestPerParameter, setLatestPerParameter] = useState(null);
    const [allThresholds, setAllThresholds] = useState([]);
    const [allAlerts, setAllAlerts] = useState([]);
    const [allDevices, setAllDevices] = useState([]);
    const [, setAllLabResults] = useState([]);

    const [medicationTrackerData, setMedicationTrackerData] = useState([]);
    const [selectedRange, setSelectedRange] = useState(7);

    const [, setError] = useState(null);
    const [consentBlocked, setConsentBlocked] = useState(null);

    const [isEditingDiagnosis, setIsEditingDiagnosis] = useState(false);
    const [diagnosisValue, setDiagnosisValue] = useState("");

    const [isEditingPatientInfo, setIsEditingPatientInfo] = useState(false);

    const [firstNameValue, setFirstNameValue] = useState("");
    const [middleNameValue, setMiddleNameValue] = useState("");
    const [lastNameValue, setLastNameValue] = useState("");
    const [dobValue, setDobValue] = useState("");
    const [genderValue, setGenderValue] = useState("");

    const [isEditingContactInfo, setIsEditingContactInfo] = useState(false);
    const [phoneNumberValue, setPhoneNumberValue] = useState("");
    const [emailValue, setEmailValue] = useState("");
    const [addressLine1Value, setAddressLine1Value] = useState("");
    const [addressLine2Value, setAddressLine2Value] = useState("");
    const [cityValue, setCityValue] = useState("");
    const [countryValue, setCountryValue] = useState("");

    const [latestFeeling, setLatestFeeling] = useState(null);
    const [feelingLoading, setFeelingLoading] = useState(true);
    const [feelingError, setFeelingError] = useState(false);

    const getFeelingColor = (feeling) => {
        switch (feeling?.toLowerCase()) {
            case 'stable':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'moderate':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'unwell':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const fetchLatestFeeling = useCallback(async () => {
        if (!pmrn) return;

        try {
            setFeelingLoading(true);
            setFeelingError(false);

            const response = await axios.get(
                `${API_BASE_URL}/patient-daily-feelings/patient/${pmrn}/latest`
            );

            setLatestFeeling(response.data);

        } catch (error) {
            if (error.response?.status === 404) {
                setLatestFeeling(null);
            } else {
                setFeelingError(true);
            }
        } finally {
            setFeelingLoading(false);
        }
    }, [pmrn, API_BASE_URL]);

    const renderFeelingIndicator = () => {
        if (feelingLoading) {
            return (
                <div className="flex items-center space-x-1" title="Latest feeling">
                    <div className="w-3 h-3 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs text-gray-500">Loading...</span>
                </div>
            );
        }

        if (feelingError) {
            return (
                <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded border border-red-200"
                        title="Latest feeling">
                    Error
                </span>
            );
        }

        if (latestFeeling) {
            const getLastUpdatedText = (dateString) => {
                if (!dateString) return '';

                try {
                    const recordedDate = new Date(dateString);
                    const today = new Date();
                    const yesterday = new Date(today);
                    yesterday.setDate(yesterday.getDate() - 1);

                    const recordedDateOnly = new Date(recordedDate.getFullYear(), recordedDate.getMonth(), recordedDate.getDate());
                    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

                    const time = recordedDate.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    });

                    if (recordedDateOnly.getTime() === todayOnly.getTime()) {
                        return `Last updated Today ${time}`;
                    } else if (recordedDateOnly.getTime() === yesterdayOnly.getTime()) {
                        return `Last updated Yesterday ${time}`;
                    } else {
                        const date = recordedDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: recordedDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
                        });
                        return `Last updated ${date} ${time}`;
                    }
                } catch (error) {
                    return '';
                }
            };

            return (
            <div className="flex flex-col items-end" title="Latest feeling">
                <span className={`px-2 py-1 rounded text-xs font-semibold border ${getFeelingColor(latestFeeling.feelingTag)}`}>
                    {latestFeeling.feelingTag}
                </span>
                {latestFeeling.recordedAt && (
                    <span className="text-xs text-gray-400 mt-1">
                        {getLastUpdatedText(latestFeeling.recordedAt)}
                    </span>
                )}
            </div>
        );
    }
    return (
            <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded border border-gray-200"
                    title="Latest feeling">
                Not recorded
            </span>
        );
    };

    const fetchClinicianId = useCallback(async (userId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/clinicians/user/${userId}`);
            if (!response.ok) throw new Error("Failed to fetch clinician info");

            const data = await response.json();

            setCurrentClinician(data);
            return data.id;
        } catch (error) {
            console.error("Error fetching clinician ID:", error);
            return null;
        }
    }, [API_BASE_URL]);

    useEffect(() => {
        if (loggedInUserId) {
            fetchClinicianId(loggedInUserId).then((id) => {
                if (id) {
                    setClinicianId(id);
                }
            });
        }
    }, [loggedInUserId, fetchClinicianId]);

    useEffect(() => {
        const parseConsentBlocked = (error) => {
            const raw = error?.response?.data?.detail;
            const payload = typeof raw === 'object' && raw !== null ? raw : {};
            if (
                error?.response?.status === 403 &&
                payload.error_code === 'PATIENT_CONSENT_REQUIRED'
            ) {
                return {
                    message:
                        payload.detail ||
                        'This patient must accept required consent before you can view their data.',
                    missing: payload.missing_consent_types || [],
                    pmrn: payload.PMRN,
                };
            }
            return null;
        };

        const fetchCriticalData = async () => {
            if (!patientId) return;

            setLoading(true);
            setConsentBlocked(null);
            try {
                const token = getToken();
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const patientRes = await axios.get(`${API_BASE_URL}/patients/${patientId}`, { headers });
                const patientData = patientRes.data;

                setPatient(patientData);
                setPmrn(patientData.MRN);
                setDiagnosisValue(patientData.diagnosis || "");
                setFirstNameValue(patientData.firstName);
                setMiddleNameValue(patientData.middleName);
                setLastNameValue(patientData.lastName);
                setDobValue(patientData.dob);
                setGenderValue(patientData.gender);

                setPhoneNumberValue(patientData.phoneNumber || "");
                setEmailValue(patientData.email || "");
                setAddressLine1Value(patientData.addressLine1 || "");
                setAddressLine2Value(patientData.addressLine2 || "");
                setCityValue(patientData.city || "");
                setCountryValue(patientData.country || "");

                if (patientData.patient_doctors && patientData.patient_doctors.length > 0) {
                    const clinicianPromises = patientData.patient_doctors.map(async (pd) => {
                        if (pd.clinician_id) {
                            const res = await axios.get(`${API_BASE_URL}/clinicians/${pd.clinician_id}`);
                            return res.data;
                        }
                        return null;
                    });

                    const clinicianResults = await Promise.all(clinicianPromises);
                    const validClinicians = clinicianResults.filter(c => c !== null);
                    setClinicians(validClinicians);
                } else {
                    console.log('No assigned clinicians found for this patient');
                    setClinicians([]);
                }

            } catch (error) {
                const blocked = parseConsentBlocked(error);
                if (blocked) {
                    setConsentBlocked(blocked);
                    setPatient(null);
                    setPmrn(null);
                    setClinicians([]);
                    return;
                }
                console.error('Error fetching patient or clinicians:', error);
                setClinicians([]);
                setError('Failed to load patient details');
            } finally {
                setLoading(false);
            }
        };

        fetchCriticalData();
    }, [patientId, API_BASE_URL]);

    useEffect(() => {
        const fetchSecondaryData = async () => {
            if (!pmrn) return;

            try {
                const [readingsRes, devicesRes, latestPerParamRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/readings/patient/${pmrn}`),
                    axios.get(`${API_BASE_URL}/devices/?pmrn=${pmrn}`),
                    axios.get(`${API_BASE_URL}/readings/patient/${pmrn}/latest-per-parameter`).catch(() => ({ data: null })),
                ]);

                if (readingsRes.data?.data) {
                    setAllReadings(readingsRes.data.data);
                } else if (Array.isArray(readingsRes.data)) {
                    setAllReadings(readingsRes.data);
                }

                setAllDevices(devicesRes.data);

                const raw = latestPerParamRes?.data?.data;
                if (raw && typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).length > 0) {
                    setLatestPerParameter(raw);
                } else {
                    setLatestPerParameter(null);
                }
            } catch (error) {
                console.error('Error fetching secondary data:', error);
                setLatestPerParameter(null);
            }
        };

        fetchSecondaryData();
    }, [pmrn, API_BASE_URL]);

    const fetchTertiaryData = useCallback(async () => {
        if (!pmrn) return;

        try {
            const token = getToken();
            if (!token) {
                console.error('No authentication token found');
                setLoading(false);
                return;
            }
            const headers = { Authorization: `Bearer ${token}` };
            const [appointmentsRes, medicationsRes, MedicationTrackerDataRes, thresholdsRes, alertsRes, clinical_findingsRes, labResultsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/appointments/with-relations/?pmrn=${pmrn}`, { headers }),
                axios.get(`${API_BASE_URL}/medications/?pmrn=${pmrn}`),
                axios.get(`${API_BASE_URL}/medication-intake/patient/${pmrn}`),
                axios.get(`${API_BASE_URL}/thresholds/?pmrn=${pmrn}`),
                axios.get(`${API_BASE_URL}/alerts/?pmrn=${pmrn}&limit=1000`, { headers }),
                axios.get(`${API_BASE_URL}/clinical-findings/patient/${pmrn}`),
                axios.get(`${API_BASE_URL}/lab-results/patient/${pmrn}`)
            ]);
            setAllAppointments(appointmentsRes.data);
            setAllMedications(medicationsRes.data);
            setAllThresholds(Array.isArray(thresholdsRes.data) ? thresholdsRes.data : []);
            setAllAlerts(alertsRes.data);
            setAllClinical_findings(clinical_findingsRes.data);
            setAllLabResults(labResultsRes.data);
            setMedicationTrackerData(MedicationTrackerDataRes.data);
        } catch (error) {
            console.error('Error fetching tertiary data:', error);
        }
    }, [pmrn, API_BASE_URL]);

    useEffect(() => {
        fetchTertiaryData();
    }, [fetchTertiaryData]);

    useEffect(() => {
        if (pmrn) {
            fetchLatestFeeling();
        }
    }, [pmrn, fetchLatestFeeling]);

    const handleSaveDiagnosis = async () => {
        try {
            const res = await axios.put(`${API_BASE_URL}/patients/${patient.id}`, {
                diagnosis: diagnosisValue,
            });

            if (res.status === 200) {
                setPatient(res.data);
                setIsEditingDiagnosis(false);
            }
        } catch (err) {
            console.error("Error updating diagnosis:", err);
            alert("Failed to update diagnosis.");
        }
    };

    const handleDeleteDiagnosis = async () => {
        if (!window.confirm("Are you sure you want to delete the diagnosis?")) return;

        try {
            const res = await axios.put(`${API_BASE_URL}/patients/${patient.id}/`, {
                diagnosis: null,
            });

            if (res.status === 200) {
                setPatient(res.data);
                setDiagnosisValue("");
            }
        } catch (err) {
            console.error("Error deleting diagnosis:", err);
            alert("Failed to delete diagnosis.");
        }
    };

    const fetchPatient = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/patients/${patientId}`);
            setPatient(res.data);

            setFirstNameValue(res.data.firstName);
            setMiddleNameValue(res.data.middleName);
            setLastNameValue(res.data.lastName);
            setDobValue(res.data.dob);
            setGenderValue(res.data.gender);
        } catch (err) {
            console.error("Failed to reload patient:", err);
            alert("Failed to refresh patient");
        }
    };

    const handleSavePatientInfo = async () => {
        try {
            const payload = {
                firstName: firstNameValue || patient.firstName,
                middleName: middleNameValue || patient.middleName,
                lastName: lastNameValue || patient.lastName,
                dob: dobValue || patient.dob,
                gender: genderValue || patient.gender
            };

            await axios.put(
                `${API_BASE_URL}/patients/${patient.id}`,
                payload
            );

            alert("Patient info updated");

            setIsEditingPatientInfo(false);
            fetchPatient();

        } catch (error) {
            console.error("Error updating patient:", error);
            alert("Failed to update patient");
        }
    };

    const handleSaveContactInfo = async () => {
        try {
            const payload = {
                phoneNumber: phoneNumberValue || patient.phoneNumber || "",
                email: emailValue || patient.email || "",
                addressLine1: addressLine1Value || patient.addressLine1 || "",
                addressLine2: addressLine2Value || patient.addressLine2 || "",
                city: cityValue || patient.city || "",
                country: countryValue || patient.country || ""
            };

            await axios.put(
                `${API_BASE_URL}/patients/${patient.id}`,
                payload
            );

            alert("Contact information updated");

            setIsEditingContactInfo(false);
            fetchPatient();

        } catch (error) {
            console.error("Error updating contact info:", error);
            alert("Failed to update contact information");
        }
    };

    const getAuthHeaders = () => {
        const token = getToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const openDoctorAssignModal = async () => {
        try {
            const doctorsResponse = await axios.get(`${API_BASE_URL}/clinicians/`, {
                headers: getAuthHeaders()
            });
            const allClinicians = Array.isArray(doctorsResponse.data)
                ? doctorsResponse.data
                : doctorsResponse.data?.data || [];
            const doctors = allClinicians.filter(c => c.role?.toLowerCase() === "doctor");
            setAvailableDoctors(doctors);

            if (patient?.MRN) {
                const currentDoctorsResponse = await axios.get(
                    `${API_BASE_URL}/patients/mrn/${patient.MRN}/doctors`,
                    { headers: getAuthHeaders() }
                );
                const currentDoctors = Array.isArray(currentDoctorsResponse.data)
                    ? currentDoctorsResponse.data
                    : [];
                setSelectedDoctorsToAssign(currentDoctors.map(d => d.id));
            }

            setShowDoctorAssignModal(true);
        } catch (error) {
            console.error("Error fetching doctors:", error);
            alert("Failed to load doctors");
        }
    };

    const closeDoctorAssignModal = () => {
        setShowDoctorAssignModal(false);
        setSelectedDoctorsToAssign([]);
        setAvailableDoctors([]);
    };

    const toggleDoctorSelection = (doctorId) => {
        setSelectedDoctorsToAssign(prev => {
            if (prev.includes(doctorId)) {
                return prev.filter(id => id !== doctorId);
            } else {
                return [...prev, doctorId];
            }
        });
    };

    const handleDoctorAssignment = async () => {
        if (!patient?.MRN) return;

        try {
            const currentDoctorsResponse = await axios.get(
                `${API_BASE_URL}/patients/mrn/${patient.MRN}/doctors`,
                { headers: getAuthHeaders() }
            );
            const currentDoctors = Array.isArray(currentDoctorsResponse.data)
                ? currentDoctorsResponse.data
                : [];
            const currentDoctorIds = currentDoctors.map(d => d.id);

            const doctorsToAdd = selectedDoctorsToAssign.filter(id => !currentDoctorIds.includes(id));

            const doctorsToRemove = currentDoctorIds.filter(id => !selectedDoctorsToAssign.includes(id));

            const patientMRN = patient.MRN;

            for (const doctorId of doctorsToAdd) {
                await axios.post(
                    `${API_BASE_URL}/patients/mrn/${patientMRN}/doctors/${doctorId}`,
                    {},
                    { headers: getAuthHeaders() }
                );
            }

            for (const doctorId of doctorsToRemove) {
                await axios.delete(
                    `${API_BASE_URL}/patients/mrn/${patientMRN}/doctors/${doctorId}`,
                    { headers: getAuthHeaders() }
                );
            }

            closeDoctorAssignModal();

            const patientRes = await axios.get(`${API_BASE_URL}/patients/${patientId}`);
            const updatedPatient = patientRes.data;
            setPatient(updatedPatient);

            if (updatedPatient?.patient_doctors && updatedPatient.patient_doctors.length > 0) {
                const clinicianPromises = updatedPatient.patient_doctors.map(async (pd) => {
                    if (pd.clinician_id) {
                        const res = await axios.get(`${API_BASE_URL}/clinicians/${pd.clinician_id}`);
                        return res.data;
                    }
                    return null;
                });
                const clinicianResults = await Promise.all(clinicianPromises);
                const validClinicians = clinicianResults.filter(c => c !== null);
                setClinicians(validClinicians);
            } else {
                setClinicians([]);
            }
        } catch (error) {
            console.error("Error updating doctor assignments:", error);
            alert("Failed to update doctor assignments. Please check the console for details.");
        }
    };

    const openNurseAssignModal = async () => {
        try {
            const nursesResponse = await axios.get(`${API_BASE_URL}/clinicians/`, {
                headers: getAuthHeaders()
            });
            const allClinicians = Array.isArray(nursesResponse.data)
                ? nursesResponse.data
                : nursesResponse.data?.data || [];
            const nurses = allClinicians.filter(c => c.role?.toLowerCase() === "nurse");
            setAvailableNurses(nurses);

            if (patient?.MRN) {
                const currentNursesResponse = await axios.get(
                    `${API_BASE_URL}/patients/mrn/${patient.MRN}/nurses`,
                    { headers: getAuthHeaders() }
                );
                const currentNurses = Array.isArray(currentNursesResponse.data)
                    ? currentNursesResponse.data
                    : [];
                setSelectedNursesToAssign(currentNurses.map(n => n.id));
            }

            setShowNurseAssignModal(true);
        } catch (error) {
            console.error("Error fetching nurses:", error);
            alert("Failed to load nurses");
        }
    };

    const closeNurseAssignModal = () => {
        setShowNurseAssignModal(false);
        setSelectedNursesToAssign([]);
        setAvailableNurses([]);
    };

    const toggleNurseSelection = (nurseId) => {
        setSelectedNursesToAssign(prev => {
            if (prev.includes(nurseId)) {
                return prev.filter(id => id !== nurseId);
            } else {
                return [...prev, nurseId];
            }
        });
    };

    const handleNurseAssignment = async () => {
        if (!patient?.MRN) return;

        try {
            const currentNursesResponse = await axios.get(
                `${API_BASE_URL}/patients/mrn/${patient.MRN}/nurses`,
                { headers: getAuthHeaders() }
            );
            const currentNurses = Array.isArray(currentNursesResponse.data)
                ? currentNursesResponse.data
                : [];
            const currentNurseIds = currentNurses.map(n => n.id);

            const nursesToAdd = selectedNursesToAssign.filter(id => !currentNurseIds.includes(id));

            const nursesToRemove = currentNurseIds.filter(id => !selectedNursesToAssign.includes(id));

            const patientMRN = patient.MRN;

            for (const nurseId of nursesToAdd) {
                await axios.post(
                    `${API_BASE_URL}/patients/mrn/${patientMRN}/nurses/${nurseId}`,
                    {},
                    { headers: getAuthHeaders() }
                );
            }

            for (const nurseId of nursesToRemove) {
                await axios.delete(
                    `${API_BASE_URL}/patients/mrn/${patientMRN}/nurses/${nurseId}`,
                    { headers: getAuthHeaders() }
                );
            }

            closeNurseAssignModal();

            const patientRes = await axios.get(`${API_BASE_URL}/patients/${patientId}`);
            const updatedPatient = patientRes.data;
            setPatient(updatedPatient);

            if (updatedPatient?.patient_doctors && updatedPatient.patient_doctors.length > 0) {
                const clinicianPromises = updatedPatient.patient_doctors.map(async (pd) => {
                    if (pd.clinician_id) {
                        const res = await axios.get(`${API_BASE_URL}/clinicians/${pd.clinician_id}`);
                        return res.data;
                    }
                    return null;
                });
                const clinicianResults = await Promise.all(clinicianPromises);
                const validClinicians = clinicianResults.filter(c => c !== null);
                setClinicians(validClinicians);
            } else {
                setClinicians([]);
            }
        } catch (error) {
            console.error("Error updating nurse assignments:", error);
            alert("Failed to update nurse assignments. Please check the console for details.");
        }
    };

    if (consentBlocked) {
        return (
            <div className="min-h-screen flex flex-col vitra-atmosphere">
                <Header />
                <div className="flex-1 flex items-center justify-center px-4 py-12">
                    <div className="max-w-lg w-full bg-white border border-amber-200 rounded-xl shadow-sm p-8 text-center">
                        <AlertTriangle className="mx-auto h-12 w-12 text-amber-500 mb-4" />
                        <h1 className="text-xl font-semibold text-gray-900 mb-2">
                            Patient consent required
                        </h1>
                        <p className="text-gray-600 mb-4">
                            {consentBlocked.message}
                        </p>
                        {consentBlocked.pmrn != null && (
                            <p className="text-sm text-gray-500 mb-4">PMRN: {consentBlocked.pmrn}</p>
                        )}
                        {consentBlocked.missing?.length > 0 && (
                            <div className="text-left bg-amber-50 border border-amber-100 rounded-lg p-4 mb-6">
                                <p className="text-sm font-medium text-amber-900 mb-2">
                                    Missing or withdrawn required consent:
                                </p>
                                <ul className="text-sm text-amber-800 list-disc list-inside space-y-1">
                                    {consentBlocked.missing.map((type) => (
                                        <li key={type}>{type.replace(/_/g, ' ')}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <p className="text-sm text-gray-500 mb-6">
                            Ask the patient to open the VITRA app and accept the consent form. Clinical data
                            access will be restored once required consents are granted again.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/my-patients')}
                            className="bg-teal-600 hover:bg-teal-700 text-white px-5 py-2 rounded-lg font-medium"
                        >
                            Back to My Patients
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading patient details...</p>
            </div>
          </div>
        );
    }

    if (!patient) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center">
                <p className="text-gray-600 mb-4">Patient not found</p>
                <button onClick={() => navigate('/my-patients')} className="bg-teal-600 text-white px-4 py-2 rounded">
                    Back to Patients
                </button>
            </div>
        );
    }

    const latestReading =
        allReadings && allReadings.length > 0
            ? allReadings.sort((a, b) => {
                const bDate = parseBackendDate(b.timestamp || b.created_at);
                const aDate = parseBackendDate(a.timestamp || a.created_at);
                return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
            })[0]
            : null;

    
    const formatLastTaken = (dateString) => {
        if (!dateString) return null;
        try {
            const d = parseBackendDate(dateString);
            if (!d) return null;
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const timeStr = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            if (dDate.getTime() === today.getTime()) return `today at ${timeStr}`;
            if (dDate.getTime() === yesterday.getTime()) return `yesterday at ${timeStr}`;
            if (dDate.getFullYear() === now.getFullYear()) return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${timeStr}`;
            return `${d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} at ${timeStr}`;
        } catch {
            return null;
        }
    };

    
    const getVitalFromLatestPerParam = (paramKey, fallbackKeys = []) => {
        const reading = latestPerParameter?.[paramKey] || (fallbackKeys.length ? latestPerParameter?.[fallbackKeys[0]] : null);
        if (!reading) return { value: null, unit: '', lastTaken: null };
        const keys = [paramKey, ...fallbackKeys];
        let value = null;
        for (const k of keys) {
            const v = reading[k];
            if (v !== undefined && v !== null && v !== '') {
                value = v;
                break;
            }
        }
        const lastTaken = formatLastTaken(reading.created_at);
        return { value, lastTaken };
    };

    const patientAppointments = allAppointments.filter((apt) => apt.PMRN === patient.MRN);

    const patientAlerts = allAlerts.filter((alerts) => {
        const status = (alerts.current_status || '').toLowerCase();
        return alerts.PMRN === patient.MRN && status !== 'resolved';
    });

    const patientDevices = Array.isArray(allDevices) ? allDevices : [];

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = parseBackendDate(dateString);
            if (!date) return 'N/A';
            return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        } catch {
            return 'N/A';
        }
    };

    const getConnectionStatusColor = (status) => {
        const colors = {
            'connected': 'bg-green-100 text-green-800',
            'disconnected': 'bg-red-100 text-red-800',
            'error': 'bg-yellow-100 text-yellow-800'
        };
        return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    };

    const age = calculateAge(patient.dob);

    return (
        <div className="min-h-screen flex flex-col vitra-atmosphere">
            <Header />
            <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6">
                
                <button
                    onClick={() => navigate('/my-patients')}
                    className="mb-4 flex items-center text-teal-600 hover:text-teal-700"
                >
                    <ChevronLeft className="w-5 h-5 mr-1" strokeWidth={2} aria-hidden="true" />
                    Back to Patients
                </button>

                {(patient?.isActive === false || patient?.status === "Not active") && (
                    <div className="mb-6 rounded-xl border-2 border-red-300 bg-gradient-to-r from-red-50 via-red-100 to-red-50 px-6 py-4 shadow-md">
                        <div className="flex items-center justify-center gap-3">
                            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                            <p className="text-red-800 font-bold text-lg tracking-wide text-center">
                                The patient is Inactive. Contact Admin.
                            </p>
                        </div>
                    </div>
                )}

                
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex items-start justify-between">

                        
                        <div className="flex items-center space-x-4">

                            
                            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center">

                                <span className="text-teal-700 font-bold text-3xl">
                                    {patient.firstName?.charAt(0)}
                                </span>
                            </div>

                            
                            <div className="flex items-center space-x-2 ml-2">
                                {isEditingPatientInfo ? (
                                    <div className="grid grid-cols-2 gap-4 mt-3">

                                        <input
                                            type="text"
                                            value={firstNameValue}
                                            onChange={(e) => setFirstNameValue(e.target.value)}
                                            className="border p-2 rounded-md"
                                            placeholder="First Name"
                                        />

                                        <input
                                            type="text"
                                            value={middleNameValue}
                                            onChange={(e) => setMiddleNameValue(e.target.value)}
                                            className="border p-2 rounded-md"
                                            placeholder="Middle Name"
                                        />

                                        <input
                                            type="text"
                                            value={lastNameValue}
                                            onChange={(e) => setLastNameValue(e.target.value)}
                                            className="border p-2 rounded-md"
                                            placeholder="Last Name"
                                        />

                                        <input
                                            type="date"
                                            value={dobValue}
                                            onChange={(e) => setDobValue(e.target.value)}
                                            className="border p-2 rounded-md"
                                        />

                                        <select
                                            value={genderValue}
                                            onChange={(e) => setGenderValue(e.target.value)}
                                            className="border p-2 rounded-md"
                                        >
                                            <option value="">Select gender</option>
                                            <option value="Male">Male</option>
                                            <option value="Female">Female</option>
                                        </select>

                                        <button
                                            onClick={handleSavePatientInfo}
                                            className="bg-teal-600 text-white px-3 py-2 rounded-md hover:bg-teal-700 col-span-2"
                                        >
                                            Save
                                        </button>

                                    </div>
                                ) : (
                                    <div>
                                        <h1 className="text-3xl font-bold text-gray-600">
                                            {patient.firstName} {patient.middleName} {patient.lastName}
                                        </h1>

                                        <p className="text-gray-600">Patient MRN: {patient.MRN}</p>

                                        <p className="text-gray-600">{age} years | {patient.gender}</p>

                                        
                                        <div className="flex items-center space-x-2 mt-1">
                                            <Pencil
                                                className="w-4 h-4 text-gray-600 cursor-pointer hover:text-teal-700"
                                                onClick={() => setIsEditingPatientInfo(true)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        
                        
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="space-y-2 mt-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <h4 className="text-teal-700 font-bold text-1xl flex items-center">
                                    Diagnosis:
                                    
                                    <div className="flex items-center space-x-2 ml-2">
                                        {isEditingDiagnosis ? (
                                            <X
                                                className="w-4 h-4 text-gray-600 cursor-pointer hover:text-red-600"
                                                onClick={() => {
                                                    setIsEditingDiagnosis(false);
                                                    setDiagnosisValue(patient.diagnosis || "");
                                                }}
                                            />
                                        ) : (
                                            <Pencil
                                                className="w-4 h-4 text-gray-600 cursor-pointer hover:text-teal-700"
                                                onClick={() => setIsEditingDiagnosis(true)}
                                            />
                                        )}
                                        <Trash2
                                            className="w-4 h-4 text-red-600 cursor-pointer hover:text-red-800"
                                            onClick={handleDeleteDiagnosis}
                                        />
                                    </div>
                                </h4>
                            </div>
                            
                            <div className="flex items-center">
                                {renderFeelingIndicator()}
                            </div>
                        </div>
                        {isEditingDiagnosis ? (
                            <div className="flex items-center space-x-2 mt-1">
                                <input
                                    type="text"
                                    value={diagnosisValue}
                                    onChange={(e) => setDiagnosisValue(e.target.value)}
                                    className="border border-gray-300 rounded-md p-1 w-full"
                                    placeholder="Enter diagnosis..."
                                />
                                <button
                                    onClick={handleSaveDiagnosis}
                                    className="bg-teal-600 text-white px-3 py-1 rounded-md hover:bg-teal-700"
                                >
                                    Save
                                </button>
                            </div>
                        ) : (
                            <p className="ml-1">
                                {patient.diagnosis || (
                                    <span className="text-gray-400 italic">---</span>
                                )}
                            </p>
                        )}
                    </div>

                    
                    {(() => {
                        const bpReading = latestPerParameter?.systolic_bp || latestPerParameter?.diastolic_bp;
                        const bpValue = bpReading?.systolic_bp != null && bpReading?.diastolic_bp != null
                            ? `${bpReading.systolic_bp}/${bpReading.diastolic_bp}` : (latestReading?.systolic_bp != null && latestReading?.diastolic_bp != null
                                ? `${latestReading.systolic_bp}/${latestReading.diastolic_bp}` : (latestReading?.blood_pressure || latestReading?.bloodPressure || null));
                        const bpLastTaken = bpReading ? formatLastTaken(bpReading.created_at) : null;
                        const hr = getVitalFromLatestPerParam('heart_rate');
                        const glu = getVitalFromLatestPerParam('blood_glucose');
                        const o2 = getVitalFromLatestPerParam('oxygen_saturation');
                        const stats = [
                            { label: 'Blood Pressure', value: bpValue, unit: '', lastTaken: bpLastTaken, bgClass: 'bg-orange-50', labelClass: 'text-orange-800/90' },
                            { label: 'Heart Rate', value: hr.value ?? latestReading?.heart_rate ?? latestReading?.heartRate ?? null, unit: 'bpm', lastTaken: hr.lastTaken, bgClass: 'bg-red-50', labelClass: 'text-red-800/90' },
                            { label: 'Blood Glucose', value: glu.value ?? latestReading?.blood_glucose ?? latestReading?.bloodGlucose ?? null, unit: 'mg/dL', lastTaken: glu.lastTaken, bgClass: 'bg-blue-50', labelClass: 'text-blue-800/90' },
                            { label: 'O₂ Saturation', value: o2.value ?? latestReading?.oxygen_saturation ?? null, unit: '%', lastTaken: o2.lastTaken, bgClass: 'bg-green-50', labelClass: 'text-green-800/90' },
                        ];
                        return (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                                {stats.map((s) => (
                                    <div key={s.label} className={`${s.bgClass} p-4 rounded-xl border border-gray-100/80 shadow-sm`}>
                                        <p className={`text-sm font-medium ${s.labelClass}`}>{s.label}</p>
                                        <p className="mt-1 text-xl font-semibold text-gray-900 tracking-tight">
                                            {s.value != null && s.value !== '' ? <>{s.value}{s.unit && <span className="text-base font-normal text-gray-600 ml-1">{s.unit}</span>}</> : '–'}
                                        </p>
                                        {s.lastTaken && (
                                            <p className="mt-2 text-xs text-gray-500">Last taken — {s.lastTaken}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        );
                    })()}

                </div>

                <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
                    <UnderlineTabs
                        value={activeTab}
                        onChange={setActiveTab}
                        layoutId="patient-detail-tabs"
                        options={[
                            'overview',
                            'vitals',
                            'medications',
                            'alerts',
                            'appointments',
                            'thresholds',
                            'lab results',
                            'notes',
                        ].map((tab) => ({
                            id: tab,
                            label: tab.charAt(0).toUpperCase() + tab.slice(1),
                        }))}
                    />

                    <div className="p-6">
                                                <KeepAlivePanel id="overview" activeId={activeTab} visited={visitedTabs}>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                
                                <MedicalHistorySection
                                    allMedicalHistory={allMedicalHistory}
                                    setAllMedicalHistory={setAllMedicalHistory}
                                    clinicianId={clinicianId}
                                    pmrn={patient.MRN}
                                    API_BASE_URL={API_BASE_URL}
                                />

                                
                                <ClinicalFindingsSection
                                    allclinical_findings={allclinical_findings}
                                    setAllClinical_findings={setAllClinical_findings}
                                    clinicianId={clinicianId}
                                    pmrn={patient.MRN}
                                    API_BASE_URL={API_BASE_URL}
                                />

                                
                                <div className="border border-teal-300 rounded-xl p-6 space-y-3 md:col-span-1 bg-gradient-to-br from-white to-teal-50/30">
                                    <h3 className="font-bold text-teal-700 mb-4 text-lg flex items-center gap-2">
                                        <BarChart3 className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                                        Latest Vitals
                                    </h3>

                                    {(() => {

                                        const bpReading = latestPerParameter?.systolic_bp || latestPerParameter?.diastolic_bp;
                                        const bpValue = bpReading?.systolic_bp && bpReading?.diastolic_bp
                                            ? `${bpReading.systolic_bp}/${bpReading.diastolic_bp}`
                                            : null;

                                        const bpLastTaken = formatLastTaken(bpReading?.created_at);

                                        const vitals = [
                                            {
                                                label: 'Blood Glucose',
                                                ...getVitalFromLatestPerParam('blood_glucose'),
                                                unit: 'mg/dL',
                                                color: 'orange',
                                                icon: <VitalIcon icon={VITAL_CONFIG['Blood glucose'].icon} className="w-5 h-5" />,
                                            },
                                            {
                                                label: 'Heart Rate',
                                                ...getVitalFromLatestPerParam('heart_rate'),
                                                unit: 'bpm',
                                                color: 'red',
                                                icon: <VitalIcon icon={VITAL_CONFIG['Heart Rate'].icon} className="w-5 h-5" />,
                                            },
                                            {
                                                label: 'Blood Pressure',
                                                value: bpValue,
                                                lastTaken: bpLastTaken,
                                                unit: 'mmHg',
                                                color: 'blue',
                                                icon: <VitalIcon icon={VITAL_CONFIG['Blood Pressure'].icon} className="w-5 h-5" />,
                                            },
                                            {
                                                label: 'SpO2',
                                                ...getVitalFromLatestPerParam('oxygen_saturation'),
                                                unit: '%',
                                                color: 'purple',
                                                icon: <VitalIcon icon={VITAL_CONFIG.SpO2.icon} className="w-5 h-5" />,
                                            },
                                            {
                                                label: 'Weight',
                                                ...getVitalFromLatestPerParam('weight_kg'),
                                                unit: 'kg',
                                                color: 'green',
                                                icon: <VitalIcon icon={VITAL_CONFIG.Weight.icon} className="w-5 h-5" />,
                                            },
                                            {
                                                label: 'Steps',
                                                ...getVitalFromLatestPerParam('steps'),
                                                unit: 'steps',
                                                color: 'yellow',
                                                icon: <VitalIcon icon={VITAL_CONFIG.Steps.icon} className="w-5 h-5" />,
                                            },
                                            {
                                                label: 'Sleep Hours',
                                                ...getVitalFromLatestPerParam('sleep_hours'),
                                                unit: 'hrs',
                                                color: 'indigo',
                                                icon: <VitalIcon icon={VITAL_CONFIG['Sleep Hours'].icon} className="w-5 h-5" />,
                                            },
                                            {
                                                label: 'Workouts',
                                                ...getVitalFromLatestPerParam('workouts'),
                                                unit: '',
                                                color: 'pink',
                                                icon: <VitalIcon icon={VITAL_CONFIG.Workouts.icon} className="w-5 h-5" />,
                                            },
                                            {
                                                label: 'Calories',
                                                ...getVitalFromLatestPerParam('calories'),
                                                unit: 'kcal',
                                                color: 'amber',
                                                icon: <VitalIcon icon={VITAL_CONFIG.Calories.icon} className="w-5 h-5" />,
                                            },
                                        ];

                                        return vitals.map((vital, i) => {
                                            const iconBgClasses = {
                                                orange: 'bg-orange-100 text-orange-600',
                                                red: 'bg-red-100 text-red-600',
                                                blue: 'bg-blue-100 text-blue-600',
                                                purple: 'bg-purple-100 text-purple-600',
                                                green: 'bg-green-100 text-green-600',
                                                yellow: 'bg-yellow-100 text-yellow-600',
                                                indigo: 'bg-indigo-100 text-indigo-600',
                                                pink: 'bg-pink-100 text-pink-600',
                                                amber: 'bg-amber-100 text-amber-600',
                                            };

                                            return (
                                                <div
                                                    key={i}
                                                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                                                >
                                                    <div className="flex items-center space-x-3">
                                                        <div className={`p-2 rounded-lg ${iconBgClasses[vital.color]}`}>
                                                            {vital.icon}
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-medium text-gray-500">{vital.label}</p>
                                                            <p className="text-base font-bold text-gray-900">
                                                                {vital.value !== undefined && vital.value !== null && vital.value !== 0
                                                                    ? `${vital.value} ${vital.unit}`
                                                                    : '–'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {vital.lastTaken && (
                                                        <div className={`text-xs text-gray-500 font-medium`}>
                                                            Last: {vital.lastTaken}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>

                                
                                
                                <div>
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-xl font-bold text-gray-800">Connected Devices</h3>
                                        <button className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 transition inline-flex items-center">
                                            <Plus className="w-5 h-5 mr-2" strokeWidth={2} aria-hidden="true" />
                                            Add New Device
                                        </button>
                                    </div>

                                    {patientDevices.length > 0 ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {patientDevices.map((device) => (
                                                <div key={device.id} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition">
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                                                                <Smartphone className="w-6 h-6 text-teal-600" strokeWidth={2} aria-hidden="true" />
                                                            </div>
                                                            <div>
                                                                <h4 className="font-semibold text-gray-800">{device.deviceName}</h4>
                                                                <p className="text-sm text-gray-500">{device.deviceType}</p>
                                                            </div>
                                                        </div>
                                                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${getConnectionStatusColor(device.connectionStatus)}`}>
                                                            {device.connectionStatus}
                                                        </span>
                                                    </div>

                                                    <div className="space-y-2 text-sm">
                                                        {device.manufacturer && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">Manufacturer:</span>
                                                                <span className="text-gray-800 font-medium">{device.manufacturer}</span>
                                                            </div>
                                                        )}
                                                        {device.modelNumber && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">Model:</span>
                                                                <span className="text-gray-800 font-medium">{device.modelNumber}</span>
                                                            </div>
                                                        )}
                                                        {device.serialNumber && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">Serial:</span>
                                                                <span className="text-gray-800 font-medium">{device.serialNumber}</span>
                                                            </div>
                                                        )}
                                                        {device.connectionType && (
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">Connection:</span>
                                                                <span className="text-gray-800 font-medium">{device.connectionType}</span>
                                                            </div>
                                                        )}
                                                        {device.battery_level_pct !== null && device.battery_level_pct !== undefined && (
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-gray-600">Battery:</span>
                                                                <div className="flex items-center space-x-2">
                                                                    <div className="w-16 bg-gray-200 rounded-full h-2">
                                                                        <div
                                                                            className={`h-2 rounded-full ${device.battery_level_pct > 50 ? 'bg-green-500' :
                                                                                device.battery_level_pct > 20 ? 'bg-yellow-500' : 'bg-red-500'
                                                                                }`}
                                                                            style={{ width: `${device.battery_level_pct}%` }}
                                                                        ></div>
                                                                    </div>
                                                                    <span className="text-gray-800 font-medium">{device.battery_level_pct}%</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {device.lastConnectedAt && (
                                                            <div className="flex justify-between pt-2 border-t border-gray-100">
                                                                <span className="text-gray-600">Last Sync:</span>
                                                                <span className="text-gray-800 font-medium">{formatDateTime(device.lastConnectedAt)}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="mt-4 flex space-x-2">
                                                        <button className="flex-1 bg-teal-50 text-teal-600 px-3 py-2 rounded text-sm hover:bg-teal-100 transition">
                                                            Sync Now
                                                        </button>
                                                        <button className="flex-1 bg-gray-50 text-gray-600 px-3 py-2 rounded text-sm hover:bg-gray-100 transition">
                                                            Settings
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 bg-gray-50 rounded-lg">
                                            <Smartphone className="w-16 h-16 mx-auto text-gray-400 mb-4" strokeWidth={1.5} aria-hidden="true" />
                                            <p className="text-gray-600 mb-4">No devices connected yet</p>
                                            <button className="bg-teal-600 text-white px-6 py-2 rounded hover:bg-teal-700">
                                                Add First Device
                                            </button>
                                        </div>
                                    )}
                                </div>

                                
                                <div className="border border-teal-300 rounded p-4 space-y-4 md:col-span-2">
                                    <ContactsSection
                                        patient={patient}
                                        API_BASE_URL={API_BASE_URL}
                                        onUpdate={setPatient}
                                    />
                                </div>
                            </div>
</KeepAlivePanel>

                        
                        <KeepAlivePanel id="vitals" activeId={activeTab} visited={visitedTabs}>

                        {patient?.MRN ? (
                            <VitalsChart pmrn={patient.MRN} API_BASE_URL={API_BASE_URL} />
                        ) : null}
</KeepAlivePanel>

                        <KeepAlivePanel id="medications" activeId={activeTab} visited={visitedTabs}>

                            <div>
                                <MedicationTrackerChart
                                    data={medicationTrackerData}
                                    medications={allMedications}
                                    selectedRange={selectedRange}
                                    onRangeChange={setSelectedRange}
                                />
                                <MedicationManager patientMRN={pmrn} />
                            </div>
</KeepAlivePanel>

                        <KeepAlivePanel id="alerts" activeId={activeTab} visited={visitedTabs}>

                            <AlertsTab
                                alerts={patientAlerts}
                                setAlerts={setAllAlerts}
                                API_BASE_URL={API_BASE_URL}
                                loggedInUserId={loggedInUserId}
                                pmrn={patient.MRN}
                            />
</KeepAlivePanel>

                        
                        <KeepAlivePanel id="lab results" activeId={activeTab} visited={visitedTabs}>
                            {patient?.MRN ? (
                                <LabResultsSection pmrn={patient.MRN} API_BASE_URL={API_BASE_URL} />
                            ) : null}
                        </KeepAlivePanel>

                        <KeepAlivePanel id="appointments" activeId={activeTab} visited={visitedTabs}>

                            <AppointmentsTab
                                patientAppointments={patientAppointments}
                                pmrn={patient.MRN}
                                patientName={`${patient.firstName} ${patient.lastName}`}
                                API_BASE_URL={API_BASE_URL}
                                onRefresh={fetchTertiaryData}
                            />
</KeepAlivePanel>

                        <KeepAlivePanel id="thresholds" activeId={activeTab} visited={visitedTabs}>

                            <ThresholdsTab
                                allThresholds={allThresholds.filter(threshold => threshold.PMRN === patient.MRN)}
                                setAllThresholds={setAllThresholds}
                                patientId={patientId}
                                pmrn={patient.MRN}
                                clinicianId={clinicianId}
                                API_BASE_URL={API_BASE_URL}
                            />
</KeepAlivePanel>

                        <KeepAlivePanel id="notes" activeId={activeTab} visited={visitedTabs}>

                            <NotesSection
                                pmrn={patient.MRN}
                                API_BASE_URL={API_BASE_URL}
                            />
</KeepAlivePanel>
                    </div>
                </div>

                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" >
                    
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-gray-800">Assigned Clinicians</h3>
                            <div className="flex gap-2">
                                {currentClinician && currentClinician.role?.toLowerCase() === "doctor" && (
                                    <>
                                        <button
                                            onClick={openDoctorAssignModal}
                                            className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                            title="Assign Doctors"
                                        >
                                            <UserPlus size={16} />
                                            <span>Assign Doctors</span>
                                        </button>
                                        <button
                                            onClick={openNurseAssignModal}
                                            className="flex items-center gap-1 px-3 py-1 text-sm bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                                            title="Assign Nurses"
                                        >
                                            <UserPlus size={16} />
                                            <span>Assign Nurses</span>
                                        </button>
                                    </>
                                )}
                                {currentClinician && currentClinician.role?.toLowerCase() === "nurse" && (
                                    <button
                                        onClick={openNurseAssignModal}
                                        className="flex items-center gap-1 px-3 py-1 text-sm bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                                        title="Assign Nurses"
                                    >
                                        <UserPlus size={16} />
                                        <span>Assign Nurses</span>
                                    </button>
                                )}
                            </div>
                        </div>
                        {clinicians && clinicians.length > 0 ? (
                            clinicians.map((clinician, index) => {
                                const isDoctor = clinician.role?.toLowerCase() === "doctor";
                                const isNurse = clinician.role?.toLowerCase() === "nurse";
                                const speciality = clinician.speciality || "General";
                                return (
                                    <div key={index} className="flex items-center space-x-4 mb-4">
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDoctor ? "bg-teal-100" : isNurse ? "bg-pink-100" : "bg-gray-100"
                                            }`}>
                                            <span className={`font-semibold ${isDoctor ? "text-teal-700" : isNurse ? "text-pink-700" : "text-gray-700"
                                                }`}>
                                                {isDoctor ? "Dr" : isNurse ? "Nurse" : "Clin"}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-800">
                                                {clinician.firstName} {clinician.lastName}
                                            </p>
                                            <p className="text-sm text-gray-600">
                                                
                                                {isDoctor
                                                    ? `Doctor - ${speciality}`
                                                    : isNurse
                                                        ? `Nurse - ${speciality}`
                                                        : clinician.role}
                                            </p>
                                            <p className="text-sm text-gray-500">{clinician.contact || clinician.phoneNumber || ""}</p>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="text-gray-500">No clinicians assigned</p>
                        )}
                    </div>

                    
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-gray-800">Contact Information</h3>
                            {!isEditingContactInfo && (
                                <button
                                    onClick={() => setIsEditingContactInfo(true)}
                                    className="flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-600 px-3 py-2 rounded-md transition-colors"
                                >
                                    <Pencil size={16} />
                                    <span>Edit</span>
                                </button>
                            )}
                        </div>

                        {isEditingContactInfo ? (
                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        value={phoneNumberValue}
                                        onChange={(e) => setPhoneNumberValue(e.target.value)}
                                        className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                        placeholder="Phone Number"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        value={emailValue}
                                        onChange={(e) => setEmailValue(e.target.value)}
                                        className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                        placeholder="Email"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                                    <input
                                        type="text"
                                        value={addressLine1Value}
                                        onChange={(e) => setAddressLine1Value(e.target.value)}
                                        className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                        placeholder="Address Line 1"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                                    <input
                                        type="text"
                                        value={addressLine2Value}
                                        onChange={(e) => setAddressLine2Value(e.target.value)}
                                        className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                        placeholder="Address Line 2"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                        <input
                                            type="text"
                                            value={cityValue}
                                            onChange={(e) => setCityValue(e.target.value)}
                                            className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            placeholder="City"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                        <input
                                            type="text"
                                            value={countryValue}
                                            onChange={(e) => setCountryValue(e.target.value)}
                                            className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                            placeholder="Country"
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-2 pt-4 border-t">
                                    <button
                                        onClick={handleSaveContactInfo}
                                        className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 font-medium flex items-center justify-center gap-2"
                                    >
                                        <Check size={16} />
                                        Save
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsEditingContactInfo(false);
                                            setPhoneNumberValue(patient.phoneNumber || "");
                                            setEmailValue(patient.email || "");
                                            setAddressLine1Value(patient.addressLine1 || "");
                                            setAddressLine2Value(patient.addressLine2 || "");
                                            setCityValue(patient.city || "");
                                            setCountryValue(patient.country || "");
                                        }}
                                        className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 font-medium flex items-center justify-center gap-2"
                                    >
                                        <X size={16} />
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="flex items-center text-gray-700">
                                    <Phone className="w-5 h-5 mr-2 text-gray-400" strokeWidth={2} aria-hidden="true" />
                                    <span>{patient.phoneNumber || 'N/A'}</span>
                                </div>
                                <div className="flex items-center text-gray-700">
                                    <Mail className="w-5 h-5 mr-2 text-gray-400" strokeWidth={2} aria-hidden="true" />
                                    <span>{patient.email || 'N/A'}</span>
                                </div>
                                <div className="flex items-start text-gray-700">
                                    <MapPin className="w-5 h-5 mr-2 mt-0.5 text-gray-400 flex-shrink-0" strokeWidth={2} aria-hidden="true" />
                                    <span>
                                        {[
                                            patient.addressLine1,
                                            patient.addressLine2,
                                            patient.city,
                                            patient.country
                                        ].filter(Boolean).join(', ') || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div >

            </main >

            <Footer />

            
            {patient && (
            <Modal open={showDoctorAssignModal} onClose={closeDoctorAssignModal} align="start">
                    <div className="bg-white rounded-2xl shadow-2xl border border-blue-100 w-full max-w-2xl max-h-[86vh] overflow-hidden flex flex-col">
                        <DialogHeader
                            title="Assign Doctors"
                            subtitle={`Patient: ${patient.firstName} ${patient.lastName}`}
                            onClose={closeDoctorAssignModal}
                            tone="muted"
                            sticky
                        />

                        <DialogBody className="p-6">
                            <p className="text-sm text-gray-600 mb-4">
                                Select doctors to assign to this patient. You can select multiple doctors.
                            </p>

                            <div className="space-y-2">
                                {availableDoctors.length === 0 ? (
                                    <p className="text-center text-gray-500 py-8">No doctors available</p>
                                ) : (
                                    availableDoctors.map((doctor) => (
                                        <label
                                            key={doctor.id}
                                            className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedDoctorsToAssign.includes(doctor.id)
                                                    ? "border-blue-500 bg-blue-50"
                                                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedDoctorsToAssign.includes(doctor.id)}
                                                onChange={() => toggleDoctorSelection(doctor.id)}
                                                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                            />
                                            <div className="ml-3 flex-1">
                                                <p className="font-medium text-gray-800">
                                                    Dr. {doctor.firstName} {doctor.lastName}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {doctor.speciality || "General Practice"} • {doctor.gender || "N/A"}
                                                </p>
                                            </div>
                                            {selectedDoctorsToAssign.includes(doctor.id) && (
                                                <Check className="text-blue-600" size={20} />
                                            )}
                                        </label>
                                    ))
                                )}
                            </div>
                        </DialogBody>

                        <DialogFooter className="sm:justify-stretch">
                            <button
                                type="button"
                                onClick={closeDoctorAssignModal}
                                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDoctorAssignment}
                                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                            >
                                Save Assignment ({selectedDoctorsToAssign.length} selected)
                            </button>
                        </DialogFooter>
                    </div>
            </Modal>
            )}

            
            {patient && (
            <Modal open={showNurseAssignModal} onClose={closeNurseAssignModal} align="start">
                    <div className="bg-white rounded-2xl shadow-2xl border border-pink-100 w-full max-w-2xl max-h-[86vh] overflow-hidden flex flex-col">
                        <DialogHeader
                            title="Assign Nurses"
                            subtitle={`Patient: ${patient.firstName} ${patient.lastName}`}
                            onClose={closeNurseAssignModal}
                            tone="muted"
                            sticky
                        />

                        <DialogBody className="p-6">
                            <p className="text-sm text-gray-600 mb-4">
                                Select nurses to assign to this patient. You can select multiple nurses.
                            </p>

                            <div className="space-y-2">
                                {availableNurses.length === 0 ? (
                                    <p className="text-center text-gray-500 py-8">No nurses available</p>
                                ) : (
                                    availableNurses.map((nurse) => (
                                        <label
                                            key={nurse.id}
                                            className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedNursesToAssign.includes(nurse.id)
                                                    ? "border-pink-500 bg-pink-50"
                                                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedNursesToAssign.includes(nurse.id)}
                                                onChange={() => toggleNurseSelection(nurse.id)}
                                                className="w-5 h-5 text-pink-600 rounded focus:ring-2 focus:ring-pink-500"
                                            />
                                            <div className="ml-3 flex-1">
                                                <p className="font-medium text-gray-800">
                                                    {nurse.firstName} {nurse.lastName}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    Nurse • {nurse.gender || "N/A"}
                                                </p>
                                            </div>
                                            {selectedNursesToAssign.includes(nurse.id) && (
                                                <Check className="text-pink-600" size={20} />
                                            )}
                                        </label>
                                    ))
                                )}
                            </div>
                        </DialogBody>

                        <DialogFooter className="sm:justify-stretch">
                            <button
                                type="button"
                                onClick={closeNurseAssignModal}
                                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleNurseAssignment}
                                className="flex-1 px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 font-medium"
                            >
                                Save Assignment ({selectedNursesToAssign.length} selected)
                            </button>
                        </DialogFooter>
                    </div>
            </Modal>
            )}
        </div >
    );
};

export default PatientDetailPage;
